import React, { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCw, Send, MessageSquare, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { api } from "../../lib/api";

function formatDateTimeShort(iso) {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat("es-ES", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export default function AdminWhatsApp() {
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null); // phone
  const [messages, setMessages] = useState([]);
  const [loadingConvs, setLoadingConvs] = useState(false);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [simBody, setSimBody] = useState("");
  const [simFrom, setSimFrom] = useState("+34600000000");
  const [sending, setSending] = useState(false);
  const [aiModal, setAiModal] = useState(null); // { drafts: [...], sourceId }
  const [aiLoading, setAiLoading] = useState(false);
  const bottomRef = useRef(null);

  const loadConversations = useCallback(async () => {
    setLoadingConvs(true);
    try {
      const r = await api.get("/admin/whatsapp/conversations?limit=100");
      setConversations(r.data.items || []);
    } catch (e) {
      toast.error("No se pudieron cargar las conversaciones");
    } finally {
      setLoadingConvs(false);
    }
  }, []);

  const loadMessages = useCallback(async (phone) => {
    if (!phone) {
      setMessages([]);
      return;
    }
    setLoadingMsgs(true);
    try {
      const r = await api.get(
        `/admin/whatsapp/conversations/${encodeURIComponent(phone)}`,
      );
      setMessages(r.data.items || []);
    } catch (e) {
      toast.error("No se pudo cargar la conversación");
    } finally {
      setLoadingMsgs(false);
    }
  }, []);

  useEffect(() => {
    loadConversations();
    const t = setInterval(() => {
      loadConversations();
      if (selected) loadMessages(selected);
    }, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  useEffect(() => {
    if (selected) loadMessages(selected);
  }, [selected, loadMessages]);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [messages.length]);

  const onSelect = (phone) => {
    setSelected(phone);
    setSimFrom(phone);
  };

  const sendSimulate = async () => {
    const body = (simBody || "").trim();
    if (!body) return;
    const from = (simFrom || "").trim();
    if (!from.startsWith("+")) {
      toast.error("Número inválido: usa formato E.164 (+34...).");
      return;
    }
    setSending(true);
    try {
      await api.post("/admin/whatsapp/simulate", { from_phone: from, body });
      setSimBody("");
      // If new conversation, select it now
      setSelected(from);
      await loadMessages(from);
      await loadConversations();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "No se pudo enviar el mensaje simulado",
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="admin-whatsapp" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight">WhatsApp</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Conversaciones automatizadas + simulador. Configura Twilio en
            <code className="text-xs mx-1 px-1 bg-neutral-100 rounded">.env</code>
            para activar respuestas reales.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => {
            loadConversations();
            if (selected) loadMessages(selected);
          }}
          data-testid="admin-whatsapp-refresh"
          className="gap-2"
        >
          <RefreshCw className="h-4 w-4" /> Refrescar
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 min-h-[600px]">
        {/* Left: conversation list */}
        <div className="md:col-span-1 rounded-2xl border bg-white overflow-hidden">
          <div className="px-4 py-3 border-b bg-neutral-50 text-xs font-bold uppercase tracking-widest text-neutral-500">
            Conversaciones
          </div>
          <div
            data-testid="admin-whatsapp-conversation-list"
            className="divide-y max-h-[720px] overflow-y-auto"
          >
            {loadingConvs && conversations.length === 0 && (
              <div className="p-6 text-sm text-neutral-500">Cargando…</div>
            )}
            {!loadingConvs && conversations.length === 0 && (
              <div className="p-6 text-sm text-neutral-500">
                Aún no hay conversaciones. Usa el simulador para probar.
              </div>
            )}
            {conversations.map((c) => (
              <button
                key={c.phone}
                data-testid={`admin-whatsapp-conv-${c.phone}`}
                onClick={() => onSelect(c.phone)}
                className={`w-full text-left px-4 py-3 transition-colors ${
                  selected === c.phone
                    ? "bg-emerald-50"
                    : "hover:bg-neutral-50"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-sm">{c.phone}</span>
                  {c.escalated && (
                    <Badge className="bg-amber-500 text-white text-[10px]">
                      Sin resolver
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-neutral-500 mt-1 line-clamp-1">
                  {c.last_body}
                </div>
                <div className="text-[11px] text-neutral-400 mt-1 flex items-center gap-1">
                  <span>{formatDateTimeShort(c.last_at)}</span>
                  <span>·</span>
                  <span>{c.count} msgs</span>
                  {c.last_channel === "simulator" && (
                    <>
                      <span>·</span>
                      <span className="text-emerald-700">Simulado</span>
                    </>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Right: message timeline + simulator input */}
        <div className="md:col-span-2 rounded-2xl border bg-white flex flex-col overflow-hidden">
          <div className="px-4 py-3 border-b bg-neutral-50 flex items-center justify-between">
            <div className="text-xs font-bold uppercase tracking-widest text-neutral-500">
              {selected ? `Conversación con ${selected}` : "Simulador de mensaje"}
            </div>
            <MessageSquare className="h-4 w-4 text-neutral-400" />
          </div>

          {/* Simulator input row */}
          <div className="p-4 border-b bg-neutral-50/40 space-y-2">
            <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,180px)_1fr_auto] gap-2 items-start">
              <Input
                data-testid="admin-whatsapp-sim-from"
                value={simFrom}
                onChange={(e) => setSimFrom(e.target.value.trim())}
                placeholder="+34612345678"
                className="text-sm"
              />
              <Textarea
                data-testid="admin-whatsapp-sim-body"
                value={simBody}
                onChange={(e) => setSimBody(e.target.value)}
                placeholder="Escribe un mensaje como si fueras un cliente…"
                rows={2}
                className="text-sm resize-none"
              />
              <Button
                onClick={sendSimulate}
                disabled={sending || !simBody.trim()}
                data-testid="admin-whatsapp-sim-send"
                className="rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white gap-2 whitespace-nowrap"
              >
                <Send className="h-4 w-4" />
                {sending ? "Enviando…" : "Enviar simulado"}
              </Button>
            </div>
            <p className="text-[11px] text-neutral-500">
              El simulador ejecuta la misma IA que el webhook real de WhatsApp,
              pero sin llamar a Twilio.
            </p>
          </div>

          {/* Timeline */}
          <div
            data-testid="admin-whatsapp-timeline"
            className="flex-1 min-h-[400px] max-h-[560px] overflow-y-auto p-4 space-y-3"
          >
            {loadingMsgs && messages.length === 0 && (
              <div className="text-sm text-neutral-500">Cargando…</div>
            )}
            {!loadingMsgs && !selected && (
              <div className="text-sm text-neutral-500">
                Selecciona una conversación o envía un mensaje simulado para empezar.
              </div>
            )}
            {selected && !loadingMsgs && messages.length === 0 && (
              <div className="text-sm text-neutral-500">
                Aún no hay mensajes en esta conversación.
              </div>
            )}
            {messages.map((m) => {
              const isOut = m.direction === "out";
              return (
                <div
                  key={m.id}
                  data-testid={`admin-whatsapp-msg-${m.direction}`}
                  className={`max-w-[80%] ${isOut ? "ml-auto" : ""}`}
                >
                  <div
                    className={`rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap ${
                      isOut
                        ? "bg-emerald-500 text-white"
                        : "bg-neutral-100 text-neutral-900"
                    }`}
                  >
                    {m.body}
                  </div>
                  <div
                    className={`flex items-center gap-2 mt-1 text-[11px] text-neutral-500 ${
                      isOut ? "justify-end" : ""
                    }`}
                  >
                    <span>{formatDateTimeShort(m.created_at)}</span>
                    {m.channel === "simulator" && (
                      <span className="rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 px-2 py-0.5">
                        Simulado
                      </span>
                    )}
                    {!isOut && m.intent && (
                      <span className="rounded-full bg-neutral-100 px-2 py-0.5">
                        {m.intent}
                      </span>
                    )}
                    {m.escalated && !isOut && (
                      <span className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5">
                        Escalado
                      </span>
                    )}
                    {!isOut && (
                      <button
                        type="button"
                        data-testid={`admin-ai-whatsapp-draft-btn-${m.id}`}
                        disabled={aiLoading}
                        onClick={async () => {
                          setAiLoading(true);
                          const t = toast.loading("IA generando borradores…");
                          try {
                            const { data } = await api.post(
                              "/admin/ai/whatsapp/draft-reply",
                              { inbound_message_id: m.id },
                            );
                            setAiModal({ drafts: data.drafts || [], sourceId: m.id });
                            toast.success("3 borradores listos", { id: t });
                          } catch (err) {
                            toast.error(
                              err?.response?.data?.detail === "ai_timeout"
                                ? "IA tardó demasiado"
                                : "IA no disponible",
                              { id: t },
                            );
                          } finally {
                            setAiLoading(false);
                          }
                        }}
                        className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 px-2 py-0.5 disabled:opacity-60"
                      >
                        <Sparkles className="h-3 w-3" /> Draft con IA
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>
        </div>
      </div>
      {aiModal && (
        <div
          role="dialog"
          aria-label="Borradores IA"
          data-testid="admin-ai-whatsapp-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/40 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setAiModal(null);
          }}
        >
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#00953B]" />
                <h3 className="font-black text-lg">Borradores IA</h3>
              </div>
              <button
                data-testid="admin-ai-whatsapp-modal-close"
                onClick={() => setAiModal(null)}
                className="h-8 w-8 grid place-items-center rounded-full hover:bg-neutral-100"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-xs text-neutral-500">
              3 respuestas sugeridas. Toca «Editar y enviar» para llevar una al simulador y ajustarla antes de enviar.
            </p>
            <div className="space-y-3">
              {aiModal.drafts.map((d, i) => (
                <div
                  key={i}
                  data-testid={`admin-ai-whatsapp-draft-${i}`}
                  className="rounded-xl border border-neutral-200 p-3 space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs uppercase tracking-wide text-neutral-500">
                      Tono: {d.tone}
                    </span>
                  </div>
                  <p className="text-sm">{d.text}</p>
                  <Button
                    type="button"
                    size="sm"
                    data-testid={`admin-ai-whatsapp-draft-use-${i}`}
                    onClick={() => {
                      setSimBody(d.text);
                      setAiModal(null);
                      toast.success("Borrador cargado en el simulador");
                    }}
                    className="bg-[#00953B] hover:bg-[#007a30] rounded-full h-8 px-3 text-xs"
                  >
                    Editar y enviar
                  </Button>
                </div>
              ))}
            </div>
            <div className="pt-2 flex justify-between border-t border-neutral-100">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={aiLoading}
                data-testid="admin-ai-whatsapp-modal-regenerate"
                onClick={async () => {
                  setAiLoading(true);
                  const t = toast.loading("Regenerando…");
                  try {
                    const { data } = await api.post(
                      "/admin/ai/whatsapp/draft-reply",
                      { inbound_message_id: aiModal.sourceId },
                    );
                    setAiModal({ drafts: data.drafts || [], sourceId: aiModal.sourceId });
                    toast.success("Nuevos borradores", { id: t });
                  } catch (err) {
                    toast.error("IA no disponible", { id: t });
                  } finally {
                    setAiLoading(false);
                  }
                }}
                className="gap-1 rounded-full"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${aiLoading ? "animate-spin" : ""}`} />
                Regenerar
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                data-testid="admin-ai-whatsapp-modal-close-btn"
                onClick={() => setAiModal(null)}
                className="rounded-full"
              >
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
